import {
  Crown,
  Users,
  MessageSquare,
  LayoutDashboard,
  Network,
  Brain,
  UserPlus,
  Settings,
  ArrowLeftRight,
  Share2,
  CheckCircle,
  Activity,
  Eye,
  History,
} from "lucide-react"
import { AgentIcon } from "@/components/agents/agent-icon"
import { cn } from "@/lib/utils"

interface Feature {
  icon: React.ReactNode
  title: string
  description: string
}

interface FeatureShowcase {
  badge: string
  badgeIcon: React.ReactNode
  title: string
  description: string
  features: Feature[]
  mockup: React.ReactNode
  reversed?: boolean
}

/**
 * Supervisor/Lead Agent mockup component.
 * Shows a supervisor card coordinating tasks to multiple agents.
 */
function SupervisorMockup() {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-xl">
      {/* Incoming Request */}
      <div className="mb-6">
        <div className="text-xs font-medium text-neutral-500 mb-2">Incoming Request</div>
        <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-blue-500/10 p-2">
              <MessageSquare className="h-4 w-4 text-blue-500" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-neutral-700">
                "Schedule a meeting with John, draft a proposal, and update the CRM"
              </p>
              <span className="text-xs text-neutral-400 mt-1 block">via Slack</span>
            </div>
          </div>
        </div>
      </div>

      {/* Supervisor Card */}
      <div className="mb-6 rounded-lg border-2 border-primary/30 bg-primary/5 p-4">
        <div className="flex items-center gap-3 mb-3">
          <AgentIcon name="Supervisor" size="lg" rounded="lg" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-neutral-900">Supervisor</div>
            <div className="text-xs text-primary">Analyzing request...</div>
          </div>
          <div className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
            Lead Agent
          </div>
        </div>
        <div className="rounded-lg bg-white border border-primary/20 p-3">
          <div className="text-xs text-neutral-600">
            Task requires: <span className="font-medium">Calendar</span>, <span className="font-medium">Content</span>, <span className="font-medium">CRM</span>
          </div>
        </div>
      </div>

      {/* Delegating To */}
      <div>
        <div className="text-xs font-medium text-neutral-500 mb-2">Delegating to:</div>
        <div className="grid grid-cols-3 gap-2">
          {[
            { name: "Calendar", initials: "CA", color: "bg-blue-500" },
            { name: "Content", initials: "CW", color: "bg-primary" },
            { name: "CRM Bot", initials: "CR", color: "bg-secondary" },
          ].map((agent, i) => (
            <div
              key={i}
              className="rounded-lg border border-neutral-200 bg-white p-2.5 text-center"
            >
              <div className={cn("mx-auto mb-1.5 flex h-8 w-8 items-center justify-center rounded-lg text-xs font-bold text-white", agent.color)}>
                {agent.initials}
              </div>
              <div className="text-xs text-neutral-700">{agent.name}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Agent Team Grid mockup component.
 * Shows a grid of specialized agents with different roles.
 */
function AgentTeamMockup() {
  const agents = [
    { name: "Sales Assistant", initials: "SA", description: "Lead gen & outreach", status: "Active", color: "bg-primary" },
    { name: "Support Agent", initials: "SU", description: "Customer support", status: "Active", color: "bg-secondary" },
    { name: "Content Writer", initials: "CW", description: "Blog & social content", status: "Active", color: "bg-blue-500" },
    { name: "Research Bot", initials: "RB", description: "Market analysis", status: "Idle", color: "bg-purple-500" },
    { name: "HR Assistant", initials: "HR", description: "Employee queries", status: "Active", color: "bg-primary" },
    { name: "Finance Bot", initials: "FB", description: "Expense tracking", status: "Idle", color: "bg-secondary" },
  ]

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          <span className="text-sm font-medium text-neutral-700">Your AI Team</span>
        </div>
        <div className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-white">
          <UserPlus className="h-3.5 w-3.5" />
          Add Agent
        </div>
      </div>

      {/* Agent Grid */}
      <div className="grid grid-cols-2 gap-3">
        {agents.map((agent, i) => (
          <div
            key={i}
            className="rounded-lg border border-neutral-200 bg-white p-3 hover:border-primary/30 transition-colors"
          >
            <div className="flex items-center gap-2.5">
              <div className="relative">
                <div className={cn("flex h-9 w-9 items-center justify-center rounded-lg text-xs font-bold text-white", agent.color)}>
                  {agent.initials}
                </div>
                <div className={cn(
                  "absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white",
                  agent.status === "Active" ? "bg-green-500" : "bg-neutral-300"
                )} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-neutral-900 truncate">{agent.name}</div>
                <div className="text-xs text-neutral-500 truncate">{agent.description}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer Stats */}
      <div className="mt-4 flex items-center justify-between text-xs text-neutral-500">
        <span>6 agents total</span>
        <span className="text-primary">4 active now</span>
      </div>
    </div>
  )
}

/**
 * Agent Communication mockup component.
 * Shows two agents communicating with shared context.
 */
function CommunicationMockup() {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-primary" />
          <span className="text-sm font-medium text-neutral-700">Agent Collaboration</span>
        </div>
        <div className="rounded-full bg-green-500/10 px-2.5 py-1 text-xs font-medium text-green-600">
          Live
        </div>
      </div>

      {/* Communication Visualization */}
      <div className="relative mb-4">
        <div className="flex items-center justify-between">
          {/* Agent 1 */}
          <div className="rounded-lg border border-neutral-200 bg-white p-3 w-[130px]">
            <div className="flex items-center gap-2 mb-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-xs font-bold text-white">
                SA
              </div>
              <div className="text-xs font-medium text-neutral-900">Sales</div>
            </div>
            <div className="rounded bg-neutral-50 p-2 text-xs text-neutral-600">
              "Lead qualified, passing to support..."
            </div>
          </div>

          {/* Connection Line */}
          <div className="flex-1 mx-3 relative">
            <div className="h-0.5 bg-gradient-to-r from-primary via-primary to-secondary" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="rounded-full bg-white border border-neutral-200 p-1.5">
                <ArrowLeftRight className="h-3 w-3 text-primary" />
              </div>
            </div>
          </div>

          {/* Agent 2 */}
          <div className="rounded-lg border border-neutral-200 bg-white p-3 w-[130px]">
            <div className="flex items-center gap-2 mb-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary text-xs font-bold text-white">
                SU
              </div>
              <div className="text-xs font-medium text-neutral-900">Support</div>
            </div>
            <div className="rounded bg-neutral-50 p-2 text-xs text-neutral-600">
              "Received! Starting onboarding..."
            </div>
          </div>
        </div>
      </div>

      {/* Shared Context */}
      <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
        <div className="flex items-center gap-2 mb-2">
          <Share2 className="h-4 w-4 text-primary" />
          <span className="text-xs font-medium text-primary">Shared Context</span>
        </div>
        <div className="space-y-1.5">
          {[
            { label: "Customer", value: "Acme Corp" },
            { label: "Intent", value: "Product inquiry" },
            { label: "Priority", value: "High" },
          ].map((item, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className="text-neutral-500">{item.label}</span>
              <span className="text-neutral-700 font-medium">{item.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Task Handoff Indicator */}
      <div className="mt-3 flex items-center gap-2 text-xs text-neutral-500">
        <CheckCircle className="h-3.5 w-3.5 text-green-500" />
        <span>Task handoff successful</span>
      </div>
    </div>
  )
}

/**
 * Command Center mockup component.
 * Shows a dashboard with agent network monitoring.
 */
function CommandCenterMockup() {
  return (
    <div className="rounded-xl border border-neutral-700 bg-neutral-900 p-4 shadow-xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-neutral-800">
        <div className="flex items-center gap-2">
          <div className="rounded-lg bg-primary/20 p-2">
            <LayoutDashboard className="h-4 w-4 text-primary" />
          </div>
          <span className="text-sm font-medium text-white">Command Center</span>
        </div>
        <div className="flex items-center gap-1 text-xs text-neutral-400">
          <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
          All systems online
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        {[
          { label: "Active Agents", value: "6", color: "text-white" },
          { label: "Tasks Today", value: "47", color: "text-primary" },
          { label: "Handoffs", value: "12", color: "text-secondary" },
          { label: "Avg Response", value: "1.2s", color: "text-blue-400" },
        ].map((stat, i) => (
          <div key={i} className="rounded-lg bg-neutral-800 p-2.5 text-center">
            <div className={cn("text-lg font-bold", stat.color)}>{stat.value}</div>
            <div className="text-xs text-neutral-500">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Mini Network Diagram */}
      <div className="rounded-lg bg-neutral-800 p-3 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Network className="h-3.5 w-3.5 text-neutral-500" />
          <span className="text-xs text-neutral-500">Agent Network</span>
        </div>
        <div className="flex items-center justify-center gap-2">
          {/* Central node */}
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/20 text-xs font-bold text-primary border border-primary/30">
            S
          </div>
          {/* Connection lines */}
          <div className="flex flex-col gap-1">
            {[1, 2, 3].map((_, i) => (
              <div key={i} className="h-0.5 w-6 bg-neutral-600 rounded" />
            ))}
          </div>
          {/* Agent nodes */}
          <div className="flex flex-col gap-1">
            {["SA", "SU", "CW"].map((initials, i) => (
              <div key={i} className="flex h-5 w-8 items-center justify-center rounded bg-neutral-700 text-xs font-medium text-neutral-300">
                {initials}
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-1">
            {[1, 2, 3].map((_, i) => (
              <div key={i} className="h-0.5 w-4 bg-neutral-700 rounded" />
            ))}
          </div>
          <div className="flex flex-col gap-1">
            {["RB", "HR", "FB"].map((initials, i) => (
              <div key={i} className="flex h-5 w-8 items-center justify-center rounded bg-neutral-700 text-xs font-medium text-neutral-300">
                {initials}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Activity Feed */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Activity className="h-3.5 w-3.5 text-neutral-500" />
          <span className="text-xs text-neutral-500">Recent Activity</span>
        </div>
        <div className="space-y-1.5">
          {[
            { agent: "Sales", action: "Completed lead qualification", time: "2m ago" },
            { agent: "Support", action: "Resolved ticket #1234", time: "5m ago" },
            { agent: "Content", action: "Published blog post", time: "12m ago" },
          ].map((activity, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <span className="text-primary font-medium">{activity.agent}</span>
                <span className="text-neutral-400">{activity.action}</span>
              </div>
              <span className="text-neutral-600">{activity.time}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const showcases: FeatureShowcase[] = [
  {
    badge: "Lead Agent",
    badgeIcon: <Crown className="h-3.5 w-3.5" />,
    title: "Intelligent Task Orchestration",
    description:
      "Your Lead Agent acts as the central coordinator, understanding requests and automatically routing tasks to the right specialized agent.",
    features: [
      {
        icon: <Network className="h-5 w-5" />,
        title: "Central Coordination",
        description: "Single point of command for all agents",
      },
      {
        icon: <Brain className="h-5 w-5" />,
        title: "Request Analysis",
        description: "Understands intent and requirements",
      },
      {
        icon: <ArrowLeftRight className="h-5 w-5" />,
        title: "Automatic Delegation",
        description: "Routes to the best agent for the job",
      },
      {
        icon: <Share2 className="h-5 w-5" />,
        title: "Context Preservation",
        description: "Maintains context across handoffs",
      },
    ],
    mockup: <SupervisorMockup />,
  },
  {
    badge: "Specialized Agents",
    badgeIcon: <Users className="h-3.5 w-3.5" />,
    title: "Build Your AI Dream Team",
    description:
      "Create specialized agents for every function - sales, support, research, content, and more. Each agent is an expert in their domain.",
    features: [
      {
        icon: <Crown className="h-5 w-5" />,
        title: "Role-Based Specialization",
        description: "Each agent has a specific function",
      },
      {
        icon: <Brain className="h-5 w-5" />,
        title: "Domain Expertise",
        description: "Trained on relevant knowledge",
      },
      {
        icon: <UserPlus className="h-5 w-5" />,
        title: "Unlimited Agents",
        description: "Scale your team without limits",
      },
      {
        icon: <Settings className="h-5 w-5" />,
        title: "Easy Customization",
        description: "Configure behavior and personality",
      },
    ],
    mockup: <AgentTeamMockup />,
    reversed: true,
  },
  {
    badge: "Real-time Collaboration",
    badgeIcon: <MessageSquare className="h-3.5 w-3.5" />,
    title: "Seamless Agent Communication",
    description:
      "Agents collaborate in real-time, sharing context and handing off tasks as needed. Complex workflows become simple conversations.",
    features: [
      {
        icon: <MessageSquare className="h-5 w-5" />,
        title: "Agent-to-Agent Messaging",
        description: "Direct communication between agents",
      },
      {
        icon: <Share2 className="h-5 w-5" />,
        title: "Shared Context",
        description: "All agents see the same information",
      },
      {
        icon: <ArrowLeftRight className="h-5 w-5" />,
        title: "Task Handoffs",
        description: "Smooth transitions between agents",
      },
      {
        icon: <Activity className="h-5 w-5" />,
        title: "Progress Tracking",
        description: "Monitor task completion in real-time",
      },
    ],
    mockup: <CommunicationMockup />,
  },
  {
    badge: "Command Center",
    badgeIcon: <LayoutDashboard className="h-3.5 w-3.5" />,
    title: "Monitor Everything in Real-Time",
    description:
      "Watch your AI team work from the Command Center. See agent status, track activities, and dive into any conversation.",
    features: [
      {
        icon: <Network className="h-5 w-5" />,
        title: "Visual Network Topology",
        description: "See how agents are connected",
      },
      {
        icon: <Eye className="h-5 w-5" />,
        title: "Live Status Updates",
        description: "Real-time agent activity monitoring",
      },
      {
        icon: <Activity className="h-5 w-5" />,
        title: "Activity Feeds",
        description: "Track all agent actions",
      },
      {
        icon: <History className="h-5 w-5" />,
        title: "Conversation History",
        description: "Review any past interaction",
      },
    ],
    mockup: <CommandCenterMockup />,
    reversed: true,
  },
]

/**
 * Features section displaying the four main multi-agent orchestration capabilities.
 * Uses alternating left/right layout with mockups and feature lists.
 */
export function FeaturesSection() {
  return (
    <section className="bg-neutral-50 py-20 lg:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-16 text-center">
          <h2 className="mb-4 text-3xl font-bold text-neutral-900 lg:text-4xl">
            Everything You Need to Orchestrate AI Teams
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-neutral-600">
            From task routing to real-time collaboration, manage your entire AI workforce
            from a single command center.
          </p>
        </div>

        {/* Feature Showcases */}
        <div className="space-y-24">
          {showcases.map((showcase, index) => (
            <div
              key={index}
              className={`grid gap-12 lg:grid-cols-2 lg:gap-16 ${
                showcase.reversed ? "lg:flex-row-reverse" : ""
              }`}
            >
              {/* Text Content */}
              <div
                className={`flex flex-col justify-center ${
                  showcase.reversed ? "lg:order-2" : ""
                }`}
              >
                <div className="mb-4 inline-flex w-fit items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-sm font-medium text-primary">
                  {showcase.badgeIcon}
                  {showcase.badge}
                </div>
                <h3 className="mb-4 text-2xl font-bold text-neutral-900 lg:text-3xl">
                  {showcase.title}
                </h3>
                <p className="mb-8 text-neutral-600">{showcase.description}</p>

                {/* Feature List */}
                <div className="grid gap-4 sm:grid-cols-2">
                  {showcase.features.map((feature, i) => (
                    <div key={i} className="flex gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        {feature.icon}
                      </div>
                      <div>
                        <div className="font-medium text-neutral-900">
                          {feature.title}
                        </div>
                        <div className="text-sm text-neutral-500">
                          {feature.description}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mockup */}
              <div className={showcase.reversed ? "lg:order-1" : ""}>
                {showcase.mockup}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
